import React, {useCallback, useContext, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {ErrorBoundary} from '@sentry/react';
import {ConfirmationContext} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {selectUserDetails} from 'src/modules/profile/user/services/user-page-slice';
import {clearUserInformation} from 'src/modules/profile/user/services/user-profile-slice';
import {useInvalidateUserContent} from 'src/modules/profile/user/tabs/content/hooks/useInvalidateUserContent';
import {getUserShortInfo} from 'src/openapi/admin/apis/GetUserShortInfoApi';
import {useAppDispatch} from 'src/store/hooks';

import {Icons, Sizes} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {Tooltip} from '@pb/kit-component-popups';
import {useShown} from '@pb/kit-hooks-use-shown';


import {ChangeAccountView} from './change-account.view';
import {useSaveChangeAccount} from './useSaveChangeAccount';

import type {ChangeAccountSubmitParams} from './change-account.view';
import type {UserSearchesReplaceResponse} from 'src/openapi/admin/models/UserSearchesReplaceResponse';

const UNSAVED_CHANGES_MESSAGE = 'You have unsaved changes. Please save or discard them before changing the account.';

export const ChangeAccountController = (props: {userId: number, accountId: number}) => {
    const {onClose, shown, onClick} = useShown();
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const {setErrorMessage} = useContext(MessageContext);
    const {changedChunks} = useContext(ConfirmationContext);

    const {shortInfo} = useSelector(selectUserDetails);

    const {canChangeUserAccount} = useAdminAccessRight();
    const {accountSubmit, accountFieldChange} = useEventTracking();
    const invalidateUserContent = useInvalidateUserContent(props.userId);

    const [migrationResult, setMigrationResult] = useState<UserSearchesReplaceResponse | null>(null);
    const {busy, event} = useSaveChangeAccount(setMigrationResult);

    const userName = shortInfo
        ? `${shortInfo.firstName} ${shortInfo.lastName}`
        : '';

    const onSubmitHandler = useCallback(
        async (params: ChangeAccountSubmitParams): Promise<boolean> => {
            accountSubmit({place: 'Change Account', submit: true});
            const {
                accountId,
                selectedTemplateUserId,
                emailAlerts,
                savedSearches,
                templates,
            } = params;
            const error = await event({
                userId: props.userId,
                userAccountChangeRequest: {
                    accountId,
                    ...(selectedTemplateUserId
                        ? {templateUserId: selectedTemplateUserId}
                        : null),
                    replaceSearches: savedSearches,
                    replaceAlerts: emailAlerts,
                    replaceLayouts: templates,
                },
            });

            if (error) {
                setErrorMessage(error);
                return false;
            }

            return true;
        }, [
            event,
            props.userId,
            accountSubmit,
            setErrorMessage,
        ],
    );

    const onFieldChange = useCallback((parameterName: string) => {
        accountFieldChange({
            section: 'Change Account',
            viewType: 'Existed',
            parameterName,
        });
    }, [accountFieldChange]);

    const onCloseClick = useCallback(() => {
        if (migrationResult) {
            dispatch(clearUserInformation());
            invalidateUserContent();
            apiDispatch(getUserShortInfo({userId: props.userId}));
        }
        onClose();
        accountSubmit({place: 'Change Account', submit: false});
    }, [
        onClose, accountSubmit, migrationResult,
        dispatch, apiDispatch, props.userId, invalidateUserContent,
    ]);

    if (canChangeUserAccount(props.accountId)) {
        return (
            <ErrorBoundary>
                <ChangeAccountView {...{
                    shown,
                    onClose: onCloseClick,
                    userId: props.userId,
                    userName,
                    onSubmitHandler,
                    busy,
                    onFieldChange,
                    migrationResult,
                }}
                />

                <Tooltip
                    color="light"
                    anchor={({anchorRef, getAnchorProps}) => (
                        <BasicIconButton
                            ref={anchorRef}
                            {...getAnchorProps()}
                            disabled={!!changedChunks.length}
                            iconProps={{
                                type: Icons.PENCIL,
                                size: Sizes.Size_14,
                            }}
                            onClick={onClick}
                        />
                    )}>
                    {changedChunks.length
                        ? UNSAVED_CHANGES_MESSAGE
                        : 'Change Account'}
                </Tooltip>
            </ErrorBoundary>
        );
    }

    return null;
};
